import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import Sidebar from './Sidebar';
import GiveSheet from './GiveSheet';
import AiDisclosure from './AiDisclosure';
import Icon from './Icon';
import { AprLockup } from './BrandMarks';
import { ShellChrome } from './ShellChrome';
import './AppShell.css';

/**
 * Application shell (BRAND_SPEC §11.1): on phones a 56 px header with the
 * Ask Pastor Rob lockup and a 44 px menu button, the AI disclosure sticky
 * under it, then the conversation. Wide screens keep the menu open as a rail.
 */
function AppShell({
  user,
  myRole,
  chats,
  activeChatId,
  currentView,
  unseen,
  hasFocused,
  sidebarOpen,
  onSidebarOpenChange,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onOpenChat,
  onFocusedStudy,
  onResumeFocused,
  onAccount,
  onContact,
  onRequestSignIn,
  onSignOut,
  lightReading,
  onToggleLightReading,
  children,
}) {
  const anyUnseen = Boolean(unseen && (unseen.archive || unseen.focused));

  // Phones: while a conversation is being read, the visible ChatView tucks
  // the header away and tightens the AI disclosure line (it never
  // disappears). Any change of view brings everything back.
  const [tucked, setTucked] = useState(false);
  useEffect(() => { setTucked(false); }, [currentView]);
  const chrome = useMemo(() => ({ tucked, setTucked }), [tucked]);

  // The header slides up by exactly its own height (safe-area inset included).
  const mainRef = useRef(null);
  const topbarRef = useRef(null);
  const menuButtonRef = useRef(null);
  useLayoutEffect(() => {
    const bar = topbarRef.current;
    const main = mainRef.current;
    if (!bar || !main) return undefined;
    const apply = () => main.style.setProperty('--shell-topbar-h', `${bar.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // Closing the menu hands focus back to the button that opened it.
  const wasOpen = useRef(sidebarOpen);
  useEffect(() => {
    if (wasOpen.current && !sidebarOpen && menuButtonRef.current && menuButtonRef.current.offsetParent) {
      menuButtonRef.current.focus({ preventScroll: true });
    }
    wasOpen.current = sidebarOpen;
  }, [sidebarOpen]);

  return (
    <div className="app-shell">
      <Sidebar
        user={user}
        myRole={myRole}
        chats={chats}
        activeChatId={activeChatId}
        currentView={currentView}
        unseen={unseen}
        hasFocused={hasFocused}
        open={sidebarOpen}
        onClose={() => onSidebarOpenChange(false)}
        onNewChat={onNewChat}
        onSelectChat={onSelectChat}
        onDeleteChat={onDeleteChat}
        onOpenChat={onOpenChat}
        onFocusedStudy={onFocusedStudy}
        onResumeFocused={onResumeFocused}
        onAccount={onAccount}
        onContact={onContact}
        onRequestSignIn={onRequestSignIn}
        onSignOut={onSignOut}
        lightReading={lightReading}
        onToggleLightReading={onToggleLightReading}
      />
      {/* The support sheet, for every give link in the app (utils/giving.js).
          When the link that opened it is gone, focus goes to the menu button. */}
      <GiveSheet fallbackFocusRef={menuButtonRef} />

      <div className={`shell-main${tucked ? ' tucked' : ''}`} ref={mainRef}>
        <header className="shell-topbar" ref={topbarRef}>
          <span className="shell-brand"><AprLockup height={22} /></span>
          <button
            ref={menuButtonRef}
            type="button"
            className="shell-hamburger"
            onClick={() => onSidebarOpenChange(true)}
            aria-label={anyUnseen ? 'Open menu. A new answer is waiting' : 'Open menu'}
            aria-expanded={sidebarOpen}
            aria-controls="apr-menu"
          >
            <Icon name="menu" size={24} />
            {anyUnseen && <span className="shell-dot" aria-hidden="true" />}
          </button>
        </header>
        <AiDisclosure compact={tucked} />
        {/* The page's main landmark: the conversation, or whichever view is open. */}
        <main className="shell-content">
          <ShellChrome.Provider value={chrome}>{children}</ShellChrome.Provider>
        </main>
      </div>
    </div>
  );
}

/**
 * Warm warning before an unsaved conversation is discarded. Per product
 * decision: clear, kind, honest. It says only what is true for everyone:
 * the conversation isn't in their saved list, so they can't come back to it
 * here. (The team keeps every conversation, privately; how and for how long
 * is the Privacy Policy's to say, and the link here goes to it.)
 * Offers a per-device "always let go" preference.
 */
export function UnsavedModal({ isGuest, onStay, onDiscard, skipChecked, onSkipChange, onRequestSignIn }) {
  const stayRef = useRef(null);
  useEffect(() => {
    stayRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onStay(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStay]);

  return (
    <div className="apr-overlay unsaved-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onStay(); }}>
      <div className="apr-dialog unsaved-modal" role="dialog" aria-modal="true" aria-labelledby="unsaved-title">
        <span className="apr-dialog-grabber" aria-hidden="true" />
        <h3 id="unsaved-title" className="apr-dialog-title">This conversation hasn't been saved</h3>
        <p className="apr-dialog-body">
          It isn't in your saved list, so you won't be able to come back to it here.
        </p>
        {isGuest ? (
          <p className="unsaved-hint">
            To keep it in your list, <button type="button" className="unsaved-link" onClick={onRequestSignIn}>sign in</button> first,
            then press “Save chat”.
          </p>
        ) : (
          <p className="unsaved-hint">To keep it in your list, go back and press “Save chat” first.</p>
        )}
        <label className="unsaved-skip">
          <input
            type="checkbox"
            checked={skipChecked}
            onChange={(e) => onSkipChange(e.target.checked)}
          />
          <span>Don't ask again on this device. Always let conversations go.</span>
        </label>
        <div className="apr-dialog-actions unsaved-actions">
          <button type="button" className="apr-btn apr-btn--quiet unsaved-btn discard" onClick={onDiscard}>Let it go</button>
          <button ref={stayRef} type="button" className="apr-btn unsaved-btn stay" onClick={onStay}>Keep chatting</button>
        </div>
        <p className="unsaved-policy">
          <a href="/privacy" target="_blank" rel="noopener noreferrer">How we keep conversations private</a>
        </p>
      </div>
    </div>
  );
}

export default AppShell;
